import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Empregador, SolicitacaoFerias, StatusSolicitacao } from '../../api.service';
import { ajustarPeriodoListagem, filtroListagemDocumentosPadrao } from '../oficios/regras-listagem-documentos';
import { ServicoSolicitacaoAdministrativa } from '../solicitacoes/servico-solicitacao-administrativa';
import { ServicoPortariaFerias } from './servico-portaria-ferias';

@Component({
  selector: 'app-tela-listagem-ferias',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './tela-listagem-ferias.component.html'
})
export class TelaListagemFeriasComponent implements OnChanges {
  @Input() empregadores: Empregador[] = [];
  @Input() empregadorSelecionado = '';

  @Output() empregadorSelecionadoChange = new EventEmitter<string>();
  @Output() mensagem = new EventEmitter<string>();

  dataInicial = filtroListagemDocumentosPadrao().dataInicial;
  dataFinal = filtroListagemDocumentosPadrao().dataFinal;
  status: StatusSolicitacao | '' = '';
  itens: SolicitacaoFerias[] = [];
  pagina = 1;
  limite = filtroListagemDocumentosPadrao().limite;
  totalElementos = 0;
  totalPaginas = 0;
  edicaoId = '';
  private rascunhoEdicao?: SolicitacaoFerias;

  constructor(
    private readonly servico: ServicoSolicitacaoAdministrativa,
    readonly servicoPortaria: ServicoPortariaFerias
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['empregadorSelecionado']) {
      this.filtrarLista();
    }
  }

  resumoLista() {
    if (!this.totalElementos) {
      return '';
    }
    return this.totalPaginas > 1
      ? `${this.totalElementos} solicitacoes · pagina ${this.pagina} de ${this.totalPaginas}`
      : `${this.totalElementos} solicitacao(oes) neste periodo`;
  }

  alterarEmpregador(empregadorId: string) {
    this.empregadorSelecionadoChange.emit(empregadorId);
  }

  alterarDataInicial(valor: string) {
    const periodo = ajustarPeriodoListagem(valor, this.dataFinal);
    this.dataInicial = periodo.dataInicial;
    this.dataFinal = periodo.dataFinal;
  }

  alterarDataFinal(valor: string) {
    const periodo = ajustarPeriodoListagem(this.dataInicial, valor);
    this.dataInicial = periodo.dataInicial;
    this.dataFinal = periodo.dataFinal;
  }

  filtrarLista() {
    this.pagina = 1;
    this.carregarLista();
  }

  irParaPagina(pagina: number) {
    if (pagina < 1 || (this.totalPaginas > 0 && pagina > this.totalPaginas) || pagina === this.pagina) {
      return;
    }
    this.pagina = pagina;
    this.carregarLista();
  }

  carregarLista() {
    if (!this.empregadorSelecionado) {
      this.itens = [];
      this.totalElementos = 0;
      this.totalPaginas = 0;
      return;
    }
    const periodo = ajustarPeriodoListagem(this.dataInicial, this.dataFinal);
    this.dataInicial = periodo.dataInicial;
    this.dataFinal = periodo.dataFinal;
    this.servico.listarFeriasPeriodo(
      this.empregadorSelecionado,
      this.dataInicial,
      this.dataFinal,
      this.status || undefined,
      this.pagina,
      this.limite
    ).subscribe({
      next: pagina => {
        this.itens = pagina.conteudo;
        this.pagina = pagina.pagina;
        this.limite = pagina.tamanho;
        this.totalElementos = pagina.totalElementos;
        this.totalPaginas = pagina.totalPaginas;
        this.edicaoId = '';
        this.rascunhoEdicao = undefined;
      },
      error: () => this.mensagem.emit('Nao foi possivel carregar a listagem de ferias.')
    });
  }

  emEdicao(item: SolicitacaoFerias) {
    return this.edicaoId === item.id;
  }

  editar(item: SolicitacaoFerias) {
    this.edicaoId = item.id;
    this.rascunhoEdicao = { ...item };
    if (item.dataInicioPortaria) {
      item.dataInicio = item.dataInicioPortaria;
    }
    if (item.dataFimPortaria) {
      item.dataFim = item.dataFimPortaria;
    }
  }

  limparEdicao() {
    if (this.rascunhoEdicao) {
      const indice = this.itens.findIndex(item => item.id === this.rascunhoEdicao?.id);
      if (indice >= 0) {
        this.itens[indice] = { ...this.rascunhoEdicao };
      }
    }
    this.edicaoId = '';
    this.rascunhoEdicao = undefined;
  }

  salvarLista(item: SolicitacaoFerias) {
    if (!item.dataInicio || !item.dataFim || item.dataFim < item.dataInicio) {
      this.mensagem.emit('Informe um periodo valido.');
      return;
    }
    if (item.periodoAquisitivo && !this.servicoPortaria.periodoAquisitivoValido(item.periodoAquisitivo)) {
      this.mensagem.emit('Informe o periodo aquisitivo no formato ano-ano.');
      return;
    }
    this.servico.editarFerias(item).subscribe({
      next: atualizado => {
        this.mensagem.emit('Solicitacao de ferias atualizada.');
        Object.assign(item, atualizado);
        this.edicaoId = '';
        this.rascunhoEdicao = undefined;
      },
      error: erro => this.mensagem.emit(erro?.error?.detail || 'Nao foi possivel atualizar a solicitacao.')
    });
  }

  exportarPortaria(item: SolicitacaoFerias) {
    this.servicoPortaria.baixarPortariaObservable(item.id).subscribe({
      next: blob => this.servicoPortaria.baixarBlob(
        blob,
        this.servicoPortaria.nomeArquivo(item.portariaNumero, item.portariaAno)
      ),
      error: erro => this.mensagem.emit(erro?.error?.detail || 'Nao foi possivel exportar a portaria.')
    });
  }

  formatarData(valor?: string) {
    return this.servicoPortaria.formatarData(valor);
  }

  rotuloStatus(status?: StatusSolicitacao) {
    return this.servico.rotuloStatus(status);
  }

  rotuloOrigem(item: SolicitacaoFerias) {
    return item.solicitadoPor || 'Portal';
  }

  periodo(item: SolicitacaoFerias) {
    return `${this.formatarData(item.dataInicioPortaria || item.dataInicio)} a ${this.formatarData(item.dataFimPortaria || item.dataFim)}`;
  }

  portaria(item: SolicitacaoFerias) {
    return item.portariaNumero ? `${item.portariaNumero}/${item.portariaAno}` : '—';
  }
}
